import { createFileRoute } from "@tanstack/react-router";
import { useOpenBuilding } from "@/lib/open-building";
import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { cardBase } from "@/lib/card-styles";
import {
  listDataPointSources,
  type DataPointSample,
  type Zone,
} from "@/lib/api/building";
import { DATA_POINT_DOMAIN_COLOR } from "@/lib/building-domain";
import {
  dataPointLabel,
  labelsFromSources,
  unitsFromSources,
} from "@/lib/data-point-labels";
import {
  formatSignalValue,
  zoneSignalSeries,
} from "@/components/building/zones/zone-live-signals";
import {
  hour_bucket,
  sample_rows,
  fmt_ts,
  type ChartRow,
} from "@/lib/chart-data";
import { loadZoneDetail } from "./-$buildingId.zones.$zoneId.data";
import { Shell } from "@/components/control-tower/Layout";
import { SectionPanel } from "@/components/ui/section-panel";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export const Route = createFileRoute("/buildings/$buildingId/zones/$zoneId")({
  component: ZoneDetailPage,
});

// One hour-bucketed row per timestamp, one column per KB data_point series — the same
// zoneSignalSeries grouping the stat tiles read from, so the chart never invents a channel
// list of its own (#1451).
function build_chart_rows(
  series: Record<string, DataPointSample[]>,
): ChartRow[] {
  const row_map = new Map<string, ChartRow>();
  for (const samples of Object.values(series)) {
    for (const s of samples) {
      if (s.numeric_value == null) continue;
      const bucket = hour_bucket(s.recorded_at);
      const row = row_map.get(bucket) ?? { ts: bucket };
      row[s.name] = s.numeric_value;
      row_map.set(bucket, row);
    }
  }
  const sorted = [...row_map.values()].sort((a, b) =>
    (a.ts as string).localeCompare(b.ts as string),
  );
  return sample_rows(sorted, 200);
}

function ZoneDetailPage() {
  const { buildingId, zoneId } = Route.useParams();
  const openBuilding = useOpenBuilding();
  const [zone, setZone] = useState<Zone | null>(null);
  const [history, setHistory] = useState<DataPointSample[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [units, setUnits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // loadZoneDetail never rejects — on any fetch failure it resolves to { zone: null },
    // so the "not found" state renders instead of spinning, with no unhandled rejection.
    loadZoneDetail(buildingId, zoneId)
      .then(({ zone: z, history: h }) => {
        setZone(z);
        setHistory(h);
      })
      .finally(() => setLoading(false));
  }, [buildingId, zoneId]);

  useEffect(() => {
    // KB name_fi labels — a progressive enhancement over dataPointLabel's de-underscored
    // fallback; never blocks the critical zone/history load above.
    listDataPointSources(buildingId)
      .then((sources) => {
        const zone_sources = sources.filter((s) => s.source_id === zoneId);
        setLabels(labelsFromSources(zone_sources));
        setUnits(unitsFromSources(zone_sources));
      })
      .catch(() => {});
  }, [buildingId, zoneId]);

  if (loading)
    return <div className="p-6 text-muted-foreground">Ladataan…</div>;
  if (!zone)
    return <div className="p-6 text-destructive">Vyöhykettä ei löydy.</div>;

  const signal_series = zoneSignalSeries(zoneId, history);
  const signal_names = Object.keys(signal_series);
  const chart_rows = build_chart_rows(signal_series);

  return (
    <Shell title={zone.name} subtitle={`Kerros ${zone.floor}`}>
      <div className="mb-4">
        <button
          type="button"
          onClick={() => openBuilding(buildingId, "/indoor-conditions")}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Rakennus
        </button>
      </div>

      {signal_names.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {signal_names.map((name) => (
            <div key={name} className={cn(cardBase, "p-3")}>
              <p className="text-xs text-muted-foreground">
                {dataPointLabel(name, labels)}
              </p>
              <p className="text-xl font-semibold tracking-tight">
                {formatSignalValue(signal_series[name].at(-1), units[name])}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="mb-6 text-sm text-muted-foreground">Ei mittaustietoa.</p>
      )}

      {chart_rows.length > 0 && (
        <SectionPanel
          icon={TrendingUp}
          title="7 päivän historia"
          className="mt-6"
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={chart_rows}
              margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="ts"
                tickFormatter={(v) => fmt_ts(v as string)}
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 10 }} width={36} />
              <Tooltip
                labelFormatter={(v) => fmt_ts(v as string)}
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              {signal_names.map((name) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={dataPointLabel(name, labels)}
                  stroke={
                    DATA_POINT_DOMAIN_COLOR[signal_series[name][0].domain]
                  }
                  dot={false}
                  strokeWidth={1.5}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </SectionPanel>
      )}
    </Shell>
  );
}
